import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Pagination,
  Skeleton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Avatar,
  Divider,
  Link as MuiLink,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  FileDownload,
  Refresh,
  WhatsApp,
  Phone,
  Email,
  Close as CloseIcon,
  Person,
  Inventory,
  MiscellaneousServices,
  School,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  enquiryService,
  Enquiry,
  EnquiryStatus,
  EnquiryServiceProductRef,
  EnquiryUserRef,
  EnquiryBusinessRef,
} from '@/services/business/enquiryService';
import { CourseLeadsList } from './CourseLeadsList';
import moment from 'moment';

interface Props {
  businessId: string;
}

const STATUS_OPTIONS: { value: EnquiryStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_COLOR: Record<EnquiryStatus, 'default' | 'primary' | 'success' | 'warning' | 'info' | 'error' | 'secondary'> = {
  new: 'info',
  contacted: 'primary',
  in_progress: 'secondary',
  qualified: 'warning',
  converted: 'success',
  not_interested: 'default',
  closed: 'error',
};

const DAYS_OPTIONS = [
  { value: 0, label: 'All time' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const getProductRef = (e: Enquiry): EnquiryServiceProductRef | null => {
  if (!e.serviceProductId) return null;
  if (typeof e.serviceProductId === 'string') return { _id: e.serviceProductId };
  return e.serviceProductId;
};

const getBusinessRef = (e: Enquiry): EnquiryBusinessRef | null => {
  if (!e.businessId) return null;
  if (typeof e.businessId === 'string') return { _id: e.businessId };
  return e.businessId;
};

const getUserRef = (e: Enquiry): EnquiryUserRef | null => {
  if (!e.userId) return null;
  if (typeof e.userId === 'string') return { _id: e.userId };
  return e.userId;
};

const ProductLeadsList: React.FC<Props> = ({ businessId }) => {
  const theme = useTheme();

  const [leads, setLeads] = useState<Enquiry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState<EnquiryStatus | 'all'>('all');
  const [days, setDays] = useState<number>(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Detail dialog state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeLead, setActiveLead] = useState<Enquiry | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const limit = 10;

  const loadLeads = async () => {
    try {
      setIsLoading(true);
      const response = await enquiryService.myLeads({
        businessId,
        page,
        limit,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined,
        days: days > 0 ? days : undefined,
      });
      setLeads(response.data.enquiries);
      setTotalPages(response.data.pagination.pages || 1);
      setTotalItems(response.data.pagination.total || 0);
    } catch {
      // toast handled by apiClient
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, days, search, businessId]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { blob, filename } = await enquiryService.exportExcel({
        businessId,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        days: days > 0 ? days : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // toast handled by apiClient
    } finally {
      setIsExporting(false);
    }
  };

  const openDetail = async (id: string) => {
    setActiveId(id);
    try {
      setIsLoadingDetail(true);
      const response = await enquiryService.getById(id);
      setActiveLead(response.data);
    } catch {
      setActiveId(null);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setActiveId(null);
    setActiveLead(null);
    setCommentText('');
  };

  const handleStatusChange = async (status: EnquiryStatus) => {
    if (!activeLead) return;
    try {
      setIsUpdatingStatus(true);
      await enquiryService.updateStatus(activeLead._id, status);
      setActiveLead({ ...activeLead, status });
      // refresh list status as well
      setLeads((prev) => prev.map((l) => (l._id === activeLead._id ? { ...l, status } : l)));
      toast.success('Status updated');
    } catch {
      // toast handled
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!activeLead || !commentText.trim()) return;
    try {
      setIsPostingComment(true);
      const response = await enquiryService.addComment(activeLead._id, commentText.trim());
      setActiveLead(response.data);
      setCommentText('');
    } catch {
      // toast handled
    } finally {
      setIsPostingComment(false);
    }
  };

  const cardSx = {
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'primary.main',
      boxShadow: theme.shadows[3],
      transform: 'translateY(-2px)',
    },
  };

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.25,
    borderRadius: 5,
    bgcolor: 'action.hover',
    fontSize: '0.72rem',
    color: 'text.secondary',
  };

  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    leads.forEach((l) => { counts[l.status] = (counts[l.status] || 0) + 1; });
    return counts;
  }, [leads]);

  return (
    <Box>
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
          alignItems: 'center',
        }}
      >
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ flex: 1, minWidth: 220 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, email, mobile…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />,
              endAdornment: searchInput ? (
                <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
        </Box>
        <TextField
          size="small"
          select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as EnquiryStatus | 'all'); setPage(1); }}
          sx={{ minWidth: 160 }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          value={days}
          onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 160 }}
        >
          {DAYS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={loadLeads} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExport}
          disabled={isExporting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isExporting ? 'Exporting…' : 'Export Excel'}
        </Button>
      </Box>

      {/* Stats summary */}
      {!isLoading && leads.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {Object.entries(stats).map(([status, count]) => (
            <Chip
              key={status}
              size="small"
              label={`${status.replace(/_/g, ' ')}: ${count}`}
              color={STATUS_COLOR[status as EnquiryStatus] || 'default'}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          ))}
        </Box>
      )}

      {/* product services Leads list */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : leads.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No leads yet
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Enquiries from interested users will appear here.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {leads.map((lead) => {
            const product = getProductRef(lead);
            const userRef = getUserRef(lead);
            return (
              <Box
                key={lead._id}
                onClick={() => openDetail(lead._id)}
                sx={{ ...cardSx, p: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontWeight: 700, flexShrink: 0 }}>
                    {lead.name?.charAt(0).toUpperCase() || <Person />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {lead.name}
                      </Typography>
                      <Chip
                        label={lead.status.replace(/_/g, ' ')}
                        size="small"
                        color={STATUS_COLOR[lead.status]}
                        sx={{ textTransform: 'capitalize', fontWeight: 600, height: 20, fontSize: '0.68rem' }}
                      />
                      {product?.serviceType && (
                        <Chip
                          icon={product.serviceType === 'product' ? <Inventory sx={{ fontSize: 12 }} /> : <MiscellaneousServices sx={{ fontSize: 12 }} />}
                          size="small"
                          variant="outlined"
                          label={product.serviceType}
                          sx={{ textTransform: 'capitalize', height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.75 }}>
                      Interested in <strong style={{ color: theme.palette.text.primary }}>{lead.serviceTitle || product?.title || 'this service'}</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {lead.mobileNumber && (
                        <Box sx={pillSx}>
                          <Phone sx={{ fontSize: 13 }} /> {lead.mobileNumber}
                        </Box>
                      )}
                      {lead.email && (
                        <Box sx={{ ...pillSx, maxWidth: 240, overflow: 'hidden' }}>
                          <Email sx={{ fontSize: 13 }} />
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.email}</Box>
                        </Box>
                      )}
                      {lead.location?.city && (
                        <Box sx={pillSx}>
                          {lead.location.city}{lead.location.state ? `, ${lead.location.state}` : ''}
                        </Box>
                      )}
                      {userRef?.userId && (
                        <Box sx={pillSx}>
                          @{userRef.userId}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
                      {moment(lead.createdAt).fromNow()}
                    </Typography>
                    {lead.comments?.length > 0 && (
                      <Typography variant="caption" color="text.disabled">
                        {lead.comments.length} comment{lead.comments.length === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {leads.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{(page - 1) * limit + 1}</strong>–<strong>{Math.min(page * limit, totalItems)}</strong> of <strong>{totalItems}</strong> {totalItems === 1 ? 'lead' : 'leads'}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!activeId} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Lead Details</Typography>
          <IconButton size="small" onClick={closeDetail}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingDetail || !activeLead ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, fontWeight: 700 }}>
                  {activeLead.name?.charAt(0).toUpperCase() || <Person />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>{activeLead.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submitted {moment(activeLead.createdAt).format('DD MMM YYYY, h:mm A')}
                  </Typography>
                </Box>
              </Box>

              {/* Contact rows */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeLead.mobileNumber && (
                  <Button
                    component="a"
                    href={`tel:${activeLead.mobileNumber}`}
                    size="small"
                    variant="outlined"
                    startIcon={<Phone />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {activeLead.mobileNumber}
                  </Button>
                )}
                {activeLead.whatsappNumber && (
                  <Button
                    component="a"
                    href={`https://wa.me/91${activeLead.whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<WhatsApp />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    WhatsApp
                  </Button>
                )}
                {activeLead.email && (
                  <Button
                    component="a"
                    href={`mailto:${activeLead.email}`}
                    size="small"
                    variant="outlined"
                    startIcon={<Email />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Email
                  </Button>
                )}
              </Box>

              {/* Service info */}
              {(() => {
                const product = getProductRef(activeLead);
                const businessRef = getBusinessRef(activeLead);
                return (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, mb: 0.5 }}>
                      Service / Product
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {activeLead.serviceTitle || product?.title || '—'}
                    </Typography>
                    {product?.price !== undefined && product.price > 0 && (
                      <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>
                        ₹{product.price.toLocaleString('en-IN')}
                      </Typography>
                    )}
                    {businessRef?.businessName && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {businessRef.businessName}
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              {/* Profile info */}
              {(() => {
                const userRef = getUserRef(activeLead);
                const profileId = userRef?._id;
                if (!activeLead.location?.city && !profileId) return null;
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {activeLead.location?.city && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600 }}>
                          Location
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {activeLead.location.city}{activeLead.location.state ? `, ${activeLead.location.state}` : ''}
                        </Typography>
                      </Box>
                    )}
                    {profileId && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600 }}>
                          Profile
                        </Typography>
                        <MuiLink href={`/profile/${profileId}`} target="_blank" rel="noopener noreferrer" variant="body2" fontWeight={500}>
                          View profile
                        </MuiLink>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              <Divider />

              {/* Status update */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, mb: 1 }}>
                  Update Status
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={activeLead.status}
                  onChange={(e) => handleStatusChange(e.target.value as EnquiryStatus)}
                  disabled={isUpdatingStatus}
                >
                  {STATUS_OPTIONS.filter((s) => s.value !== 'all').map((opt) => (
                    <MenuItem key={opt.value} value={opt.value as EnquiryStatus}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider />

              {/* Comments */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, mb: 1 }}>
                  Notes & Comments ({activeLead.comments?.length || 0})
                </Typography>
                {(activeLead.comments || []).length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5, maxHeight: 220, overflowY: 'auto' }}>
                    {activeLead.comments.map((c) => (
                      <Box
                        key={c._id}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'action.hover',
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {c.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {c.addedBy?.name || 'You'} · {moment(c.createdAt).fromNow()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={4}
                    placeholder="Add a note (e.g., called the lead, will follow up tomorrow)…"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    disabled={isPostingComment}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!commentText.trim() || isPostingComment}
                    variant="contained"
                    sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start' }}
                  >
                    {isPostingComment ? 'Adding…' : 'Add'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const BusinessLeadsTab: React.FC<Props> = ({ businessId }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.text.secondary,
            },
          }}
        >
          <Tab icon={<Inventory sx={{ fontSize: 18 }} />} iconPosition="start" label="Products & Services" />
          <Tab icon={<School sx={{ fontSize: 18 }} />} iconPosition="start" label="Courses" />
        </Tabs>
      </Box>

      {activeTab === 0 && <ProductLeadsList businessId={businessId} />}
      {activeTab === 1 && <CourseLeadsList businessId={businessId} />}
    </Box>
  );
};
